import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getFinanceiroLogs } from '../../api';
import { cores } from '../../../styles/styleUtils';

const ROTULOS_EVENTO = {
  criacao_venda: 'Venda criada',
  alteracao_venda: 'Venda alterada',
  cancelamento_venda: 'Venda cancelada',
  criacao_receita: 'Receita criada',
  alteracao_receita: 'Receita alterada',
  baixa_receita: 'Receita recebida',
  baixa_parcial_receita: 'Baixa parcial',
  cancelamento_receita: 'Receita cancelada',
  estorno_receita: 'Recebimento estornado',
  criacao_despesa: 'Despesa criada',
  alteracao_despesa: 'Despesa alterada',
  pagamento_despesa: 'Despesa paga',
  cancelamento_despesa: 'Despesa cancelada',
  estorno_despesa: 'Pagamento estornado',
  alteracao_grupo_receitas: 'Receitas futuras alteradas',
  cancelamento_grupo_receitas: 'Receitas futuras canceladas',
  alteracao_grupo_despesas: 'Despesas futuras alteradas',
  cancelamento_grupo_despesas: 'Despesas futuras canceladas',
};

const tipoChip = (tipo) => {
  if (tipo === 'receita') return { color: 'success', label: 'Receita' };
  if (tipo === 'despesa') return { color: 'error', label: 'Despesa' };
  return { color: 'default', label: 'Venda', sx: { bgcolor: cores.laranja, color: cores.textoClaro } };
};

const formatarDataHora = (valor) => {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

const formatarData = (valor) => {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

const moeda = (valor) =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatarValorAlteracao = (campo, valor) => {
  if (valor === null || valor === undefined || valor === '—') return '—';
  if (['valor', 'valorBaixa', 'valorLiquido', 'desconto', 'acrescimo', 'encargos'].includes(campo)) {
    return moeda(valor);
  }
  if (['vencimento', 'dataRecebimento', 'dataPagamento'].includes(campo)) {
    return formatarData(valor);
  }
  return String(valor);
};

function DetalheLog({ log, onClose }) {
  if (!log) return null;
  const d = log.detalhes || {};
  const chip = tipoChip(log.tipoRegistro);

  const textoSecundario = 'rgba(255,255,255,0.72)';
  const bordaSuave = 'rgba(255,255,255,0.12)';
  const fundoCard = 'rgba(255,255,255,0.035)';

  return (
    <Dialog
      open={Boolean(log)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          color: cores.textoClaro,
          border: `1px solid ${cores.chumbo}`,
          borderTop: `4px solid ${cores.laranja}`,
          background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: cores.textoClaro,
          fontWeight: 700,
          borderBottom: `1px solid ${bordaSuave}`,
        }}
      >
        Detalhes do evento financeiro
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          color: cores.textoClaro,
          borderColor: bordaSuave,
          '& strong': { color: cores.textoClaro },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Chip size="small" color={chip.color} label={chip.label} sx={chip.sx} />
          <Typography variant="h6" sx={{ color: cores.textoClaro, fontWeight: 700 }}>
            {d.titulo || log.resumo || 'Registro financeiro'}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
            '& .MuiTypography-root': { color: cores.textoClaro },
          }}
        >
          <Typography><strong>Evento:</strong> {ROTULOS_EVENTO[log.evento] || log.evento}</Typography>
          <Typography><strong>Data/Hora:</strong> {formatarDataHora(log.dataEvento || log.createdAt)}</Typography>
          <Typography><strong>Usuário do evento:</strong> {log.user?.nome || '—'}</Typography>
          <Typography><strong>Unidade:</strong> {d.unidade || '—'}</Typography>

          {log.tipoRegistro === 'despesa' && (
            <>
              <Typography><strong>Despesa:</strong> {d.titulo || '—'}</Typography>
              <Typography><strong>Código:</strong> {d.codigo || '—'}</Typography>
              <Typography><strong>Centro de custo:</strong> {d.centroCusto || '—'}</Typography>
              <Typography><strong>Obra:</strong> {d.obra || '—'}</Typography>
            </>
          )}

          {log.tipoRegistro === 'receita' && (
            <>
              <Typography><strong>Cliente:</strong> {d.cliente || '—'}</Typography>
              <Typography><strong>Parcela:</strong> {d.parcela || '—'}</Typography>
              <Typography><strong>Forma de pagamento:</strong> {d.formaPagamento || '—'}</Typography>
              <Typography><strong>Conta:</strong> {d.conta || '—'}</Typography>
            </>
          )}

          {(log.tipoRegistro === 'receita' || log.tipoRegistro === 'despesa') && (
            <>
              <Typography><strong>Valor:</strong> {moeda(d.valor)}</Typography>
              <Typography><strong>Vencimento:</strong> {formatarData(d.vencimento)}</Typography>
              <Typography><strong>Status:</strong> {d.status || '—'}</Typography>
              <Typography><strong>Data da baixa:</strong> {formatarData(d.dataBaixa)}</Typography>
              <Typography><strong>Lançado por:</strong> {d.lancadoPor || 'Não registrado'}</Typography>
              <Typography><strong>Baixado por:</strong> {d.baixadoPor || 'Não registrado'}</Typography>
              <Typography sx={{ gridColumn: { md: '1 / -1' } }}>
                <strong>Comprovante:</strong> {d.comprovante || '—'}
              </Typography>
            </>
          )}
        </Box>

        {log.motivo && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              bgcolor: fundoCard,
              color: cores.textoClaro,
              border: `1px solid ${bordaSuave}`,
              '& .MuiAlert-icon': { color: cores.laranja },
            }}
          >
            <strong>Motivo:</strong> {log.motivo}
          </Alert>
        )}

        <Divider sx={{ my: 2, borderColor: bordaSuave }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: cores.textoClaro }}>
          Alterações realizadas
        </Typography>

        {Array.isArray(log.alteracoes) && log.alteracoes.length > 0 ? (
          <Stack spacing={1}>
            {log.alteracoes.map((alt) => (
              <Paper
                key={`${alt.campo}-${alt.label}`}
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: fundoCard,
                  color: cores.textoClaro,
                  borderColor: bordaSuave,
                  boxShadow: 'none',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: cores.textoClaro }}>
                  {alt.label}
                </Typography>
                <Typography variant="body2" sx={{ color: textoSecundario }}>
                  Antes: {formatarValorAlteracao(alt.campo, alt.antes)}
                </Typography>
                <Typography variant="body2" sx={{ color: cores.textoClaro }}>
                  Depois: {formatarValorAlteracao(alt.campo, alt.depois)}
                </Typography>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: textoSecundario }}>
            Este evento não possui alteração campo a campo para exibir.
          </Typography>
        )}

        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: textoSecundario }}>
          ID do registro: {d.registroId || log.idRegistro || '—'}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: `1px solid ${bordaSuave}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: cores.laranja,
            color: cores.textoClaro,
            '&:hover': {
              bgcolor: cores.laranja,
              filter: 'brightness(0.92)',
            },
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FinanceiroLog() {
  const [logs, setLogs] = useState([]);
  const [erro, setErro] = useState('');
  const [tipo, setTipo] = useState('todos');
  const [selecionado, setSelecionado] = useState(null);

  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      const result = await getFinanceiroLogs({ limite: 500 });
      if (!ativo) return;

      if (result?.ok === false) {
        setErro(result.msg || 'Não foi possível carregar o log financeiro.');
        setLogs([]);
        return;
      }

      setErro('');
      setLogs(Array.isArray(result) ? result : []);
    };

    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  const filtrados = useMemo(
    () => (tipo === 'todos' ? logs : logs.filter((item) => item.tipoRegistro === tipo)),
    [logs, tipo]
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6">Log Financeiro</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="venda">Vendas</MenuItem>
            <MenuItem value="receita">Receitas</MenuItem>
            <MenuItem value="despesa">Despesas</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {erro && <Alert severity="warning" sx={{ mb: 2 }}>{erro}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Registro</TableCell>
              <TableCell>Evento</TableCell>
              <TableCell>Resumo</TableCell>
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtrados.map((log) => {
              const chip = tipoChip(log.tipoRegistro);
              return (
                <TableRow key={log._id}>
                  <TableCell>{formatarDataHora(log.dataEvento || log.createdAt)}</TableCell>
                  <TableCell>{log.user?.nome || '—'}</TableCell>
                  <TableCell><Chip size="small" color={chip.color} label={chip.label} sx={chip.sx} /></TableCell>
                  <TableCell>
                    {log.detalhes?.titulo || (log.tipoRegistro === 'venda' ? 'Venda' : '—')}
                  </TableCell>
                  <TableCell>{ROTULOS_EVENTO[log.evento] || log.evento}</TableCell>
                  <TableCell>{log.resumo || '—'}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setSelecionado(log)}>Ver</Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtrados.length === 0 && !erro && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum evento financeiro registrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DetalheLog log={selecionado} onClose={() => setSelecionado(null)} />
    </Box>
  );
}

export default FinanceiroLog;
